// One ORDER section, answer first: the section's question and its answer (built
// from the tools' data) on top, then the tools that produced it — the core
// (expanded), up to two contextual specialists (compact; tap to expand), a compact
// "+ DEITY" control for the rest (tap = pin) — and the orb level's own controls
// (Step / Today / Aim) as a collapsible lens under the core.
import React, { useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Pressable, ScrollView, Text, View } from 'react-native';
import type { GearId } from '../../../olmps/ascension';
import { DISPLAY_FONT, useV10 } from '../../theme';
import { useK } from '../ui';
import { WidgetShell } from './Shell';
import { selectSection } from './select';
import { dismissOrbWidget, useInvoked } from './bus';
import type { OrbWidgetModel } from './types';

const PIN_KEY = 'olmps.orb.pins';

/** Pinned specialist ids, persisted per device. */
export function usePins(): [ReadonlySet<string>, (id: string) => void] {
  const [pins, setPins] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    AsyncStorage.getItem(PIN_KEY)
      .then((v) => {
        const arr = v ? (JSON.parse(v) as unknown) : null;
        if (Array.isArray(arr)) setPins(new Set(arr.filter((x): x is string => typeof x === 'string')));
      })
      .catch(() => {});
  }, []);
  const toggle = (id: string) =>
    setPins((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      AsyncStorage.setItem(PIN_KEY, JSON.stringify([...next])).catch(() => {});
      return next;
    });
  return [pins, toggle];
}

export interface DeckProps {
  gear: GearId;
  models: Record<string, OrbWidgetModel>;
  pins: ReadonlySet<string>;
  onPin: (id: string) => void;
  onAgent?: (agentId: string, widgetId: string) => void;
  /** The orb level's row for this letter: its question, its answer and its controls. */
  lens: { question: string; answer: string[]; widget: React.ReactNode };
}

export function SectionDeck({ gear, models, pins, onPin, onAgent, lens }: DeckProps) {
  const t = useV10();
  const k = useK();
  const invoked = useInvoked();
  const pick = selectSection(gear, models, pins, invoked);
  const [open, setOpen] = useState<string | null>(null);
  const [lensOpen, setLensOpen] = useState(false);
  useEffect(() => {
    setOpen(null);
    setLensOpen(false);
  }, [gear]);
  if (!pick.core) return null;

  const pinToggle = (id: string) => () => {
    if (invoked.has(id)) dismissOrbWidget(id);
    onPin(id);
  };
  const lensBtn = (
    <Pressable onPress={() => setLensOpen((o) => !o)} style={[{ marginTop: 3 * k, alignSelf: 'stretch' }, { outlineStyle: 'none' } as object]}>
      <Text style={{ color: t.ivoryFaint, fontFamily: DISPLAY_FONT, fontStyle: 'italic', fontSize: 10.5 * k }} numberOfLines={1}>
        {lensOpen ? '▾' : '▸'} Work on it here
      </Text>
    </Pressable>
  );

  return (
    <ScrollView style={{ alignSelf: 'stretch', flexGrow: 0 }} contentContainerStyle={{ gap: 8 * k, paddingVertical: 2 }} showsVerticalScrollIndicator={false} nestedScrollEnabled>
      <View style={{ alignSelf: 'stretch', gap: 2 * k }}>
        <Text style={{ color: t.ivoryFaint, fontFamily: DISPLAY_FONT, fontStyle: 'italic', fontSize: 11 * k, lineHeight: 14 * k }}>{lens.question}</Text>
        {lens.answer.map((a, i) => (
          <Text key={i} style={{ color: i === 0 ? t.ivory : t.ivorySoft, fontSize: (i === 0 ? 13 : 11.5) * k, lineHeight: (i === 0 ? 17 : 15) * k, fontWeight: i === 0 ? '700' : '400' }} numberOfLines={2}>
            {a}
          </Text>
        ))}
        <View style={{ height: 1, alignSelf: 'stretch', backgroundColor: 'rgba(214,180,106,0.3)', marginTop: 4 * k }} />
      </View>
      <WidgetShell
        model={pick.core}
        mode="expanded"
        onAgent={onAgent}
        footer={
          <>
            {lensBtn}
            {lensOpen ? <View style={{ alignSelf: 'stretch', marginTop: 4 * k }}>{lens.widget}</View> : null}
          </>
        }
      />
      {pick.shown.map((m) => (
        <WidgetShell
          key={m.id}
          model={m}
          mode={open === m.id ? 'expanded' : 'compact'}
          onToggle={() => setOpen((o) => (o === m.id ? null : m.id))}
          onAgent={onAgent}
          pinned={pins.has(m.id) || invoked.has(m.id)}
          onPin={pinToggle(m.id)}
        />
      ))}
      {pick.more.length ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 * k, justifyContent: 'center' }}>
          {pick.more.map((m) => (
            <Pressable
              key={m.id}
              onPress={() => onPin(m.id)}
              accessibilityLabel={`Show ${m.deityName}`}
              style={[{ height: 20 * k, paddingHorizontal: 8 * k, borderRadius: 10 * k, borderWidth: 1, borderColor: 'rgba(214,180,106,0.35)', justifyContent: 'center' }, { outlineStyle: 'none' } as object]}
            >
              <Text style={{ color: t.ivorySoft, fontFamily: DISPLAY_FONT, fontSize: 9.5 * k, letterSpacing: 1 * k }}>+ {m.deityName}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}
    </ScrollView>
  );
}
